import type { DatasourceExecutionNodeKind } from '../tableau/datasourceExecutionGraph'

export type ExecutionStoragePlane = 'durable' | 'tab'

export type ExecutionScopedRelationKind = 'temp_table' | 'temp_view'

const normalizeText = (value: unknown): string => String(value ?? '').trim()

const safePathSegment = (value: string, fallback: string): string => {
  const text = normalizeText(value)
  const cleaned = text.replace(/[^A-Za-z0-9_.-]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '')
  return cleaned || fallback
}

export const normalizeExecutionScopeId = (value: unknown): string => normalizeText(value)

export const buildExecutionScopeCacheKey = (datasetId: unknown, executionScopeId: unknown): string => {
  const dataset = normalizeText(datasetId)
  const scope = normalizeText(executionScopeId)
  if (!dataset || !scope) return ''
  return `${scope}::${dataset}`
}

export const executionScopeCacheKeyBelongsToDataset = (
  cacheKey: unknown,
  datasetId: unknown,
): boolean => {
  const key = normalizeText(cacheKey)
  const dataset = normalizeText(datasetId)
  if (!key || !dataset) return false
  return key.endsWith(`::${dataset}`)
}

export const buildExecutionScopeDuckDbPath = (datasetId: unknown, executionScopeId: unknown): string => {
  const dataset = safePathSegment(normalizeText(datasetId), 'dataset')
  const scope = safePathSegment(normalizeText(executionScopeId), 'scope')
  return `opfs://tab_execution/${scope}/${dataset}.duckdb`
}

export const buildExecutionScopeIndexedDbPath = (datasetId: unknown, executionScopeId: unknown): string => {
  const dataset = safePathSegment(normalizeText(datasetId), 'dataset')
  const scope = safePathSegment(normalizeText(executionScopeId), 'scope')
  return `idb://tab_execution__${scope}__${dataset}.duckdb`
}

export const isExecutionScopedRequest = (executionScopeId: unknown): boolean =>
  normalizeExecutionScopeId(executionScopeId).length > 0

export const isDurableAggregateBaseCandidate = (input: {
  workingSetSignature?: unknown
  grainSignature?: unknown
  measureSetSignature?: unknown
  calcParamSignature?: unknown
  windowInputSignature?: unknown
  executionSignature?: unknown
}): boolean => {
  const signatures = [
    input.workingSetSignature,
    input.grainSignature,
    input.measureSetSignature,
    input.calcParamSignature,
    input.windowInputSignature,
    input.executionSignature,
  ]
  return signatures.every((value) => normalizeText(value).length === 0)
}

export const isDurableWindowInputBaseCandidate = (input: {
  workingSetSignature?: unknown
  grainSignature?: unknown
  measureSetSignature?: unknown
  calcParamSignature?: unknown
  windowInputSignature?: unknown
  executionSignature?: unknown
}): boolean => {
  const signatures = [
    input.workingSetSignature,
    input.grainSignature,
    input.measureSetSignature,
    input.calcParamSignature,
    input.windowInputSignature,
    input.executionSignature,
  ]
  return signatures.every((value) => normalizeText(value).length === 0)
}

export const classifyDatasourceNodeStoragePlane = (input: {
  nodeKind: DatasourceExecutionNodeKind
  executionScopeId?: unknown
  workingSetSignature?: unknown
  grainSignature?: unknown
  measureSetSignature?: unknown
  calcParamSignature?: unknown
  windowInputSignature?: unknown
  executionSignature?: unknown
}): ExecutionStoragePlane => {
  if (!isExecutionScopedRequest(input.executionScopeId)) return 'durable'
  if (input.nodeKind === 'semantic_base') return 'durable'
  if (input.nodeKind === 'working_set') return 'tab'
  if (input.nodeKind === 'window_input_base') {
    return isDurableWindowInputBaseCandidate(input) ? 'durable' : 'tab'
  }
  if (input.nodeKind === 'worksheet_presentation') return 'tab'
  if (input.nodeKind === 'worksheet_interaction_cube') return 'tab'
  if (input.nodeKind === 'aggregate_base') {
    return isDurableAggregateBaseCandidate(input) ? 'durable' : 'tab'
  }
  return 'durable'
}

// Execution-scoped datasource nodes (aggregate_base, window_input_base,
// worksheet_presentation, working_set) exist precisely to cache the output of
// an expensive planner sub-query so downstream wrapper SQL can scan it cheaply.
// Materializing them as TEMP VIEWs defeats that: every reference re-executes the
// underlying SQL (often large UNION ALL / CROSS JOIN UNNEST pipelines), which
// routinely OOMs when the wrapper performs DISTINCT/GROUP BY over the
// "relation". We therefore always materialize scoped planner nodes as
// TEMP TABLEs. Durable nodes continue to use persistent `CREATE TABLE main.*`
// outside this helper.
export const resolveExecutionScopedRelationKind = (
  _nodeKind: DatasourceExecutionNodeKind | string | null | undefined,
): ExecutionScopedRelationKind => 'temp_table'

export const canMaterializeDatasourceNode = (input: {
  nodeKind: DatasourceExecutionNodeKind
  executionScopeId?: unknown
  allowDurableShapedBuilds?: boolean
  workingSetSignature?: unknown
  grainSignature?: unknown
  measureSetSignature?: unknown
  calcParamSignature?: unknown
  windowInputSignature?: unknown
  executionSignature?: unknown
}): boolean => {
  const plane = classifyDatasourceNodeStoragePlane(input)
  if (plane === 'tab') return true
  if (input.nodeKind === 'semantic_base') return true
  return Boolean(input.allowDurableShapedBuilds)
}

export const buildDurableArtifactFingerprint = (manifest: any): string => {
  if (!manifest || typeof manifest !== 'object') return ''
  const datasourceNodes = Array.isArray((manifest as any)?.datasource_nodes)
    ? (manifest as any).datasource_nodes
    : []
  const durableRelationNames = Array.from(new Set(
    datasourceNodes
      .filter((entry: any) => classifyDatasourceNodeStoragePlane({
        nodeKind: String(entry?.node_kind ?? '').trim().toLowerCase() as DatasourceExecutionNodeKind,
        executionScopeId: 'scope',
        workingSetSignature: entry?.working_set_signature,
        grainSignature: entry?.grain_signature,
        measureSetSignature: entry?.measure_set_signature,
        calcParamSignature: entry?.calc_param_signature,
        windowInputSignature: entry?.window_input_signature,
        executionSignature: entry?.execution_signature,
      }) === 'durable')
      .map((entry: any) => normalizeText(entry?.table_name))
      .filter(Boolean)
  )).sort()

  // Durable execution-plane reuse must be keyed by the shared physical artifacts, not
  // foreground/prewarm lineage aliases. `binding_generation` can carry workbook/import
  // scope metadata and may be absent on later manifest reads; using it here caused the
  // same OPFS catalog to be detached and reattached while raw/semantic relations were
  // unchanged. The semantic spec hash and durable relation names are the physical
  // execution contract for this attach layer.
  const parts = [
    normalizeText((manifest as any)?.dataset_id),
    normalizeText((manifest as any)?.opfs_path),
    normalizeText((manifest as any)?.extract_identity),
    normalizeText((manifest as any)?.semantic?.spec_hash),
    durableRelationNames.join('|'),
  ]
  return parts.join('::')
}
